import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/lib/auth-client';
import { API_URL } from '@/lib/api';

/**
 * Hook pour récupérer l'organisation du user connecté.
 *
 * Stratégie :
 *  1. L'UUID de l'org est lu depuis la session BetterAuth (`session.activeOrganizationId`)
 *     — populate côté backend via `databaseHooks.session.create.before` (cf. auth.config.ts).
 *     Pas de race condition : dès que `useSession()` est résolu, on a l'ID.
 *  2. Les métadonnées (nom, logo) sont fetchées via l'API en tâche de fond,
 *     mais ne bloquent pas `organizationId` qui est dispo instantanément.
 */
export function useOrganization() {
  const { data: session, isPending } = useSession();

  // `session.session.activeOrganizationId` est set au login par BetterAuth.
  const organizationId = (session as any)?.session?.activeOrganizationId as string | undefined;

  // Fetch les métadonnées (nom, logo, slug, isActive, etc.)
  const { data: organization, isLoading: isLoadingOrg } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      const res = await fetch(`${API_URL}/organizations/slug/finance-for-you`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 10, // 10 min — ne change quasiment jamais
  });

  return {
    organization: organization ?? (organizationId ? { id: organizationId } : null),
    organizationId,
    isLoading: isPending,
    error: null,
  };
}
